import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../AuthProvider";
import { StorePackagesPanel } from "./StorePackages";
import { StoreSettingsPanel } from "./StoreSettings";
import {
  getBusinessTypeLabel,
  inferStoreBusinessType,
  summarizeStorePackages,
} from "../utils/storePackages";

export default function Dashboard() {
  const { user, userData } = useAuth();
  const [userDoc, setUserDoc] = useState(userData || null);
  const [storeDoc, setStoreDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setUserDoc(null);
      setStoreDoc(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setUserDoc(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
    const unsubStore = onSnapshot(doc(db, "stores", user.uid), (snap) => {
      setStoreDoc(snap.exists() ? snap.data() : null);
    });

    return () => {
      unsubUser();
      unsubStore();
    };
  }, [user?.uid, userData]);

  const packageState = useMemo(
    () => summarizeStorePackages(userDoc || {}),
    [userDoc],
  );
  const activePkg = packageState.hasActivePackage ? packageState.summary : null;
  const storeBusinessType = useMemo(
    () => inferStoreBusinessType(userDoc || {}, []),
    [userDoc],
  );

  const hasStoreBasics = useMemo(
    () => Boolean(storeDoc?.displayName && storeDoc?.slug),
    [storeDoc],
  );

  if (loading) {
    return <div className="max-w-6xl mx-auto px-4 py-8">Loading...</div>;
  }

  if (!activePkg) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <StorePackagesPanel
          embedded
          title="Choose Your Store Package"
          subtitle="Start here. Pick a package, pay using the admin bank details, upload your slip, and wait for approval."
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div className="rounded-[28px] bg-gradient-to-r from-slate-950 via-indigo-900 to-fuchsia-700 p-[1px] shadow-xl">
        <div className="rounded-[27px] bg-white/95 px-6 py-6 backdrop-blur">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">
                Shop Owner Dashboard
              </div>
              <div className="mt-2 text-3xl font-black text-gray-900">
                {hasStoreBasics ? "Your store is ready to grow" : "Create your store in a few quick steps"}
              </div>
              <div className="mt-2 max-w-2xl text-sm text-gray-600">
                This works like creating a Facebook page. Add your store name,
                cover, bio, and payment details once, then you can start adding
                listings and sharing your store link.
              </div>
            </div>

            <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <div className="font-semibold">
                Active package: {activePkg.name || activePkg.packageId}
              </div>
              <div className="mt-1 text-blue-800/80">
                Limits: Course {Number(activePkg.courseLimit || 0) || "∞"} •
                Product {Number(activePkg.productLimit || 0) || "∞"} • Service{" "}
                {Number(activePkg.serviceLimit || 0) || "∞"}
              </div>
              <div className="mt-1 text-blue-800/70">
                Store mode: {getBusinessTypeLabel(storeBusinessType)}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">
                Step 1
              </div>
              <div className="mt-1 text-lg font-bold text-gray-900">
                Set your store identity
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Choose a store name, slug, and short bio so buyers understand
                your brand immediately.
              </div>
            </div>
            <div className="rounded-2xl border bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">
                Step 2
              </div>
              <div className="mt-1 text-lg font-bold text-gray-900">
                Add images and bank details
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Upload a profile image, cover image, and your own payment
                details so buyers can pay you directly.
              </div>
            </div>
            <div className="rounded-2xl border bg-gray-50 p-4">
              <div className="text-sm font-semibold text-gray-900">
                Step 3
              </div>
              <div className="mt-1 text-lg font-bold text-gray-900">
                Publish and start listing
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Save your store, then move straight into your locked business
                type and start publishing.
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/manage-listing"
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Manage Listings
            </Link>
            <Link
              to="/insights"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Open Insights
            </Link>
            <Link
              to="/store"
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Open Full Store Settings
            </Link>
          </div>
        </div>
      </div>

      <StoreSettingsPanel
        embedded
        title={hasStoreBasics ? "Edit Your Store" : "Create Your Store"}
        subtitle={
          hasStoreBasics
            ? "Update your public store details here, then use the links above to grow it."
            : "Fill these simple details once to launch your public store page."
        }
      />
    </div>
  );
}
